import { Message } from "@/lib/core/crm/types";
import { CreateTaskForm as CreateTaskFormType } from "@/lib/hooks/api/tasks";

/**
 * Maps email importance to task priority
 */
export function mapEmailImportanceToTaskPriority(
  importance: "low" | "normal" | "high"
): "low" | "medium" | "high" | "urgent" {
  switch (importance) {
    case "low":
      return "low";
    case "normal":
      return "medium";
    case "high":
      return "high";
    default:
      return "medium";
  }
}

/**
 * Formats email content for task description with proper context
 */
export function formatEmailForTaskDescription(message: Message): string {
  const formatDate = (dateString: string) => {
    try {
      return new Date(dateString).toLocaleString();
    } catch {
      return dateString;
    }
  };

  const recipients = message.recipients.map((r) => `${r.name} <${r.email}>`).join(", ");

  return `**Email Details:**
- From: ${message.sender.name} <${message.sender.email}>
- To: ${recipients}
- Date: ${formatDate(message.receivedDateTime)}
- Provider: ${message.provider}
- Importance: ${message.importance}
${message.hasAttachments ? "- Has Attachments: Yes" : ""}
${message.webLink ? `- Original Email: ${message.webLink}` : ""}

**Email Content:**
${message.body || message.bodyPreview || "No content available"}

---
*Task created from email message*`.trim();
}

/**
 * Generates a task title from email subject
 */
export function generateTaskTitleFromEmail(message: Message, prefix: string = "Follow up"): string {
  const subject = message.subject?.trim();
  if (!subject) {
    return `${prefix}: Email from ${message.sender.name}`;
  }

  // Remove common email prefixes to avoid redundancy
  const cleanSubject = subject
    .replace(/^(Re:|Fwd?:|RE:|FWD?:)\s*/i, "")
    .replace(/^(Follow up|Follow-up|Followup):\s*/i, "");

  return `${prefix}: ${cleanSubject}`;
}

/**
 * Extracts email address from sender for contact matching
 */
export function extractEmailForContactMatching(message: Message): string {
  return message.sender.email?.toLowerCase().trim() || "";
}

/**
 * Generates appropriate tags for email-based tasks
 */
export function generateEmailTaskTags(message: Message): string[] {
  const tags = ["email-followup", message.provider];

  // Add importance-based tag
  if (message.importance === "high") {
    tags.push("high-priority");
  }

  // Add attachment tag if present
  if (message.hasAttachments) {
    tags.push("has-attachments");
  }

  // Add sender domain tag for organization
  const domain = message.sender.email?.split("@")[1];
  if (domain && domain !== "gmail.com" && domain !== "outlook.com" && domain !== "yahoo.com") {
    tags.push(`domain-${domain.replace(/[^a-zA-Z0-9]/g, "-")}`);
  }

  return tags;
}

/**
 * Creates a complete task data object from an email message
 */
export function createTaskDataFromMessage(
  message: Message,
  contactId?: string,
  assignedTo?: string,
  customTitle?: string
): Partial<CreateTaskFormType> {
  return {
    title: customTitle || generateTaskTitleFromEmail(message),
    description: formatEmailForTaskDescription(message),
    contact_id: contactId || undefined,
    status: "pending",
    priority: mapEmailImportanceToTaskPriority(message.importance),
    assigned_users: assignedTo ? [assignedTo] : [],
    tags: generateEmailTaskTags(message),
  };
}

/**
 * Creates ticket field prefill data from an email message.
 *
 * Mirrors {@link createTaskDataFromMessage} for the ticket create flow:
 * subject → ticket title, formatted email body → description, resolved
 * sender contact → contact_id. Keys match the ticket form field names
 * (`title`, `description`, `contact_id`).
 */
export function createTicketDataFromMessage(
  message: Message,
  contactId?: string
): { title: string; description: string; contact_id?: string } {
  return {
    title: generateTicketTitleFromEmail(message),
    description: formatEmailForTaskDescription(message),
    contact_id: contactId || undefined,
  };
}

/**
 * Generates a ticket title from an email subject (without the task-style
 * "Follow up" prefix — a ticket title should read as the issue itself).
 */
export function generateTicketTitleFromEmail(message: Message): string {
  const subject = message.subject?.trim();
  if (!subject) {
    return `Email from ${message.sender.name || message.sender.email || "sender"}`;
  }

  return subject.replace(/^(Re:|Fwd?:|RE:|FWD?:)\s*/i, "").trim() || subject;
}

/**
 * Validates if a message is suitable for task creation
 */
export function isMessageSuitableForTask(message: Message): boolean {
  // Basic validation - message should have essential fields
  return !!(message.id && message.subject && message.sender?.email && message.receivedDateTime);
}

/**
 * Extracts key information for task context
 */
export function extractTaskContextFromMessage(message: Message) {
  return {
    messageId: message.id,
    provider: message.provider,
    inboxId: message.inboxId,
    conversationId: message.conversationId,
    webLink: message.webLink,
    receivedDateTime: message.receivedDateTime,
    senderEmail: message.sender.email,
    senderName: message.sender.name,
    subject: message.subject,
    hasAttachments: message.hasAttachments,
    importance: message.importance,
  };
}
